import type { Game } from '../../../core/game';
import type { Affix, AffixId, MasterDie } from '../../../core/types';
import { AFFIX_BY_ID, affixPoolFor } from '../../../data/affixes';
import { dieScore } from '../../../core/dice';
import type { ScreenCtx } from '../../types';
import { affixValueOf, dieLevelOf, dieMaterialOf, forgeMarksOf, materialView } from '../../abyssApi';
import { affixExplanation } from '../../affixDescriptions';
import { dieArt } from '../../diceArt';
import {
  affixTextOf,
  affixListText,
  dieCardInnerHtml,
  dieInfiniteBadges,
  dieSidesLabel,
  formatGold,
  rarityName,
} from '../../format';
import {
  type ForgeChoices,
  type ForgeOutcomeView,
  type ForgeRecipeId,
  type ForgeRecipeView,
  buildRequest,
  forgeApplyOf,
  forgeCandidatesOf,
  forgeCheckOf,
  forgePendingOf,
  forgePriceOf,
  forgeRecipesOf,
  forgeRerollChooseOf,
  isForgeableOf,
  isGrandfatherDie,
  NOT_FORGEABLE_TEXT,
  preselectConsumed,
  recipeNeeds,
} from '../../forgeApi';
import { icon } from '../../icons';
import { overlayRoot } from '../../columnScale';
import { toast } from '../../toast';
import { buildDiceViewPanel, buildHiddenNotice } from './diceViewPanel';
import { filterDice, hasActiveFilter, presentMaterials, presentSides, readDiceView, sortDice } from '../../diceView';

/**
 * Onglet « Forge » de l'Accalmie — v0.46.0 (A94, lot B). Quatrième onglet,
 * visible seulement quand `hasForge()` (l'Artisan des dés est acquis).
 *
 * **Liste des recettes** (`forgeRecipes()`) : nom, description, dés consommés,
 * prix (« N or » — `forgePrice(recette, dé)` dès qu'un dé est choisi, sinon le
 * prix de base) ; une recette verrouillée est grisée et dit « Nécessite Carnet
 * de recettes ».
 *
 * **Parcours d'une recette**, étape par étape : le **dé cible** (cartes de
 * l'inventaire, équipés compris, le Dé de l'aïeul exclu), puis selon la recette
 * l'**affixe** (Relance, Fusion), le **second dé** et son affixe (Fusion),
 * l'**affixe à ajouter** (Absolution : affixes compatibles avec le dé), enfin
 * les **dés sacrifiés** proposés par `forgeCandidates` du moins bon au meilleur,
 * pré-cochés jusqu'au nombre requis et modifiables. Le prix et le verdict de
 * `forgeCheck` (refus en français, tels que rendus par le cœur) se lisent en
 * direct sous le bouton « Forger ».
 *
 * **Relance** : après `forgeApply`, une modale à trois cartes (deux affixes
 * proposés, « Garder l'actuel ») résout par `forgeRerollChoose` ; si
 * `forgePending()` existe à l'ouverture de l'onglet, la modale se rouvre.
 * **Sublimation** : un avis dit si elle a réussi. Après chaque recette la carte
 * du dé résultat est affichée sous le bouton.
 *
 * Sélecteurs stables (e2e) : `[data-role="forge-recipe"][data-recipe="reroll"]`,
 * `[data-role="forge-target"]`, `[data-role="forge-affix"]`,
 * `[data-role="forge-other"]`, `[data-role="forge-consume"]`,
 * `[data-action="forge-apply"]`, `[data-role="forge-verdict"]`,
 * `[data-role="forge-result"]`,
 * `[data-role="forge-reroll-option"][data-choice="0|1|keep"]`.
 */

/** Choix en cours : vit au niveau du module, l'onglet est reconstruit à chaque événement. */
interface ForgeSession {
  recipe: ForgeRecipeId | null;
  dieId: string | null;
  /** Liste des dés cibles dépliée (sinon : seule la cible choisie est montrée). */
  targetOpen: boolean;
  affixIndex: number | null;
  otherId: string | null;
  otherAffixIndex: number | null;
  affixId: AffixId | null;
  consumed: Set<string>;
  /** Clé pour laquelle `consumed` a été pré-coché (changer de dé ou d'affixe repart de zéro). */
  consumeKey: string;
  /** Résultat de la dernière recette (carte du dé, avis). */
  result: { recipe: ForgeRecipeId; name: string; dieId: string; cost: number; note: string } | null;
}

const session: ForgeSession = {
  recipe: null,
  dieId: null,
  targetOpen: true,
  affixIndex: null,
  otherId: null,
  otherAffixIndex: null,
  affixId: null,
  consumed: new Set(),
  consumeKey: '',
  result: null,
};

/** Remet à zéro tout ce qui dépend du dé cible. */
function resetBelowTarget(): void {
  session.affixIndex = null;
  session.otherId = null;
  session.otherAffixIndex = null;
  session.affixId = null;
  session.consumed = new Set();
  session.consumeKey = '';
}

// ─────────────────────────────────────────────────────────────── Helpers

function findDie(game: Game, id: string | null): MasterDie | undefined {
  return id ? game.state.inventory.find((d) => d.id === id) : undefined;
}

/** Liste des cibles de la Forge : ce qui s'affiche, ce que la vue en cache, et le total forgeable. */
interface TargetList {
  dice: MasterDie[];
  hidden: number;
  total: number;
}

/**
 * Dés forgeables (cibles) ; le Dé de l'aïeul n'en fait pas partie, ni (A98) un
 * dé de campagne : seuls les dés des Corrupted GachaDice se forgent
 * (`game.isForgeable`). v0.55.8 (A119) : les dés **équipés d'abord**, dans
 * l'ordre des emplacements (1 à 7) et toujours affichés — le joueur cherche
 * souvent le dé qu'il porte —, puis les autres, filtrés et triés par la vue
 * « Tri et filtres ».
 */
function targetDice(game: Game): TargetList {
  const stats = game.getDerivedStats();
  const forgeable = game.state.inventory.filter((d) => !isGrandfatherDie(d) && isForgeableOf(game, d).ok);
  const equipped: MasterDie[] = [];
  for (const id of game.state.equipped) {
    const die = typeof id === 'string' ? forgeable.find((d) => d.id === id) : undefined;
    if (die && !equipped.includes(die)) equipped.push(die);
  }
  const others = forgeable.filter((d) => !equipped.includes(d));
  const view = readDiceView();
  const shown = sortDice(filterDice(others, view), view, (d) => dieScore(d, stats));
  return { dice: [...equipped, ...shown], hidden: others.length - shown.length, total: forgeable.length };
}

/** Indices (dans `die.affixes`) des affixes listables : les affixes nommés n'y sont pas. */
function listedAffixIndexes(die: MasterDie): number[] {
  return die.affixes.map((a, i) => ({ a, i })).filter(({ a }) => !AFFIX_BY_ID[a.id]?.named).map(({ i }) => i);
}

/** Ligne compacte d'un dé (second dé de la Fusion, dés sacrifiés). */
function rowInnerHtml(die: MasterDie, game: Game): string {
  const affixes = affixListText(die, game);
  return `
    <span class="forge-row-vig">${dieArt(die.sides, die.rarity, { size: 26, stroke: 3 })}</span>
    <span class="forge-row-main">
      <span class="die-card-name">${die.name}</span>
      <span class="forge-row-meta">${dieSidesLabel(die.sides)} · ${rarityName(die.rarity)}${dieInfiniteBadges(die, game)}</span>
      <span class="forge-row-affixes">${affixes.length > 0 ? affixes.join(' · ') : 'Sans affixe'}</span>
    </span>`;
}

/** Boutons d'affixes d'un dé (`role` : `forge-affix` ou `forge-other-affix`). */
function affixButtonsHtml(die: MasterDie, game: Game, role: string, chosen: number | null): string {
  const idx = listedAffixIndexes(die);
  if (idx.length === 0) return '<p class="forge-note">Ce dé ne porte aucun affixe.</p>';
  const marks = forgeMarksOf(die);
  return `<div class="forge-affixes">${idx
    .map((i) => {
      const tags =
        (marks.rerollSlot === i ? '<span class="die-card-mark" data-mark="reroll">Relançable</span>' : '') +
        (marks.absolvedSlot === i ? '<span class="die-card-mark die-card-mark--absolution" data-mark="absolution">Absolution</span>' : '');
      return `<button type="button" class="forge-affix f-btn f-btn--sm${chosen === i ? ' is-picked' : ''}" data-role="${role}" data-index="${i}" aria-pressed="${chosen === i}">${icon('affix')}<span>${affixTextOf(die, die.affixes[i], game)}</span>${tags}</button>`;
    })
    .join('')}</div>`;
}

function stepHtml(title: string, body: string, role?: string): string {
  return `<div class="forge-step"${role ? ` data-step="${role}"` : ''}><h4 class="forge-step-title">${title}</h4>${body}</div>`;
}

function priceText(price: number | null): string {
  return price === null ? '—' : `${formatGold(price)} or`;
}

/**
 * Le prix du Trempage suit le **niveau du dé** (A98 ; A101 : 200 000 or au
 * niveau 0, multiplié à chaque niveau) ; la carte de la recette le dit en
 * toutes lettres.
 */
function formulaHtml(recipe: ForgeRecipeId): string {
  return recipe === 'temper'
    ? '<span class="forge-recipe-formula" data-role="forge-recipe-formula">Prix : suit le niveau du dé</span>'
    : '';
}

// ─────────────────────────────────────────────────────── Modale de Relance

/** La modale est-elle déjà ouverte ? (un seul exemplaire, quel que soit le chemin d'ouverture) */
let rerollModal: HTMLElement | null = null;

/**
 * Modale de Relance (A94-15) : l'affixe actuel, deux affixes neufs proposés,
 * trois cartes. Le choix est obligatoire (il résout `forgePending`), la modale
 * ne se ferme donc ni par le voile ni par Échap : elle se rouvre à la prochaine
 * ouverture de l'onglet si on la quitte autrement.
 */
function ensureRerollModal(ctx: ScreenCtx, refresh: () => void): void {
  const { game } = ctx;
  const pending = forgePendingOf(game);
  if (!pending) {
    rerollModal?.remove();
    rerollModal = null;
    return;
  }
  if (rerollModal?.isConnected) return;
  const die = findDie(game, pending.dieId);
  if (!die) return;
  const current = die.affixes[pending.affixIndex];

  const card = (choice: '0' | '1' | 'keep', kicker: string, affix: Affix | undefined): string => {
    const text = affix ? affixTextOf(die, affix, game) : '—';
    const explain = affix ? affixExplanation({ ...affix, value: affixValueOf(game, die, affix) }) : '';
    return `<button type="button" class="forge-reroll-option f-cut" data-role="forge-reroll-option" data-choice="${choice}">
      <span class="forge-reroll-k">${kicker}</span>
      <b class="forge-reroll-affix">${text}</b>
      <small class="forge-reroll-explain">${explain}</small>
    </button>`;
  };

  const overlay = document.createElement('div');
  overlay.className = 'f-scrim forge-reroll-scrim';
  overlay.setAttribute('data-role', 'forge-reroll-modal');
  overlay.innerHTML = `
    <div class="f-modal forge-reroll-modal" role="dialog" aria-modal="true" aria-labelledby="forge-reroll-title">
      <h2 class="f-modal-title" id="forge-reroll-title">Relance</h2>
      <p class="f-modal-text">Un nouvel affixe pour « ${die.name} » : choisissez-en un, ou gardez l'actuel.</p>
      <div class="forge-reroll-options">
        ${card('0', 'Nouvel affixe', pending.options[0])}
        ${card('1', 'Nouvel affixe', pending.options[1])}
        ${card('keep', "Garder l'actuel", current)}
      </div>
    </div>`;
  overlay.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-role="forge-reroll-option"]');
    if (!btn) return;
    const raw = btn.dataset.choice;
    const choice = raw === 'keep' ? 'keep' : raw === '1' ? 1 : 0;
    const before = forgePendingOf(game);
    const resolved = forgeRerollChooseOf(game, choice);
    if (!resolved) {
      toast('La Relance n’a pas pu être résolue.', 2600, 'lock');
      return;
    }
    overlay.remove();
    rerollModal = null;
    const chosen = choice === 'keep' ? null : before?.options[choice];
    const note = chosen ? `Relance : nouvel affixe ${affixTextOf(resolved, chosen, game)}` : 'Relance : affixe actuel gardé';
    session.result = { recipe: 'reroll', name: resolved.name, dieId: resolved.id, cost: 0, note };
    // Le premier affixe relancé fixe le seul relançable de ce dé : on y revient par défaut.
    session.affixIndex = forgeMarksOf(resolved).rerollSlot ?? session.affixIndex;
    session.consumed = new Set();
    session.consumeKey = '';
    toast(note, 2600, 'affix');
    refresh();
  });
  overlayRoot().appendChild(overlay);
  rerollModal = overlay;
  overlay.querySelector<HTMLElement>('[data-role="forge-reroll-option"]')?.focus({ preventScroll: true });
}

/** Ferme la modale de Relance sans résoudre (démontage de l'Accalmie) : elle se rouvrira. */
export function closeForgeModal(): void {
  rerollModal?.remove();
  rerollModal = null;
}

// ───────────────────────────────────────────────────────────────── Onglet

/** Texte du toast d'une recette appliquée (hors Relance, résolue dans la modale). */
function outcomeNote(out: ForgeOutcomeView, affixId: AffixId | null): string {
  switch (out.recipe) {
    case 'temper':
      return `Trempage : ${out.die.name} passe au niveau ${dieLevelOf(out.die)}`;
    case 'transmute': {
      const m = dieMaterialOf(out.die);
      return `Trucage : ${out.die.name} devient ${m ? materialView(m).name : 'd’un autre Matériau'}`;
    }
    case 'fuse':
      return `Fusion réussie : ${out.die.name}`;
    case 'sublimate':
      return out.success ? 'Sublimation réussie : le dé est Enrichi' : 'Sublimation échouée : les dés sont perdus';
    case 'absolve':
      return `Absolution : ${affixId ? (AFFIX_BY_ID[affixId]?.name ?? affixId) : 'affixe'} ajouté à ${out.die.name}`;
    default:
      return 'Recette appliquée';
  }
}

export function renderForgeTab(container: HTMLElement, ctx: ScreenCtx, refresh: () => void): void {
  const { game } = ctx;
  const recipes = forgeRecipesOf(game);

  // ── Cohérence de la session avec l'état de la partie ────────────────
  if (session.dieId && !findDie(game, session.dieId)) {
    session.dieId = null;
    session.targetOpen = true;
    resetBelowTarget();
  }
  if (session.otherId && !findDie(game, session.otherId)) {
    session.otherId = null;
    session.otherAffixIndex = null;
  }
  const recipeView = recipes.find((r) => r.id === session.recipe);
  if (session.recipe && (!recipeView || !recipeView.unlocked)) session.recipe = null;
  const result = session.result && findDie(game, session.result.dieId) ? session.result : null;
  if (!result) session.result = null;

  const wrap = document.createElement('div');
  wrap.className = 'forge-tab';
  wrap.setAttribute('data-role', 'forge-tab');

  const target = findDie(game, session.dieId);
  wrap.innerHTML = `
    <header class="forge-head">
      <h2 class="forge-title">${icon('forge')}<span>Forge</span></h2>
      <p class="forge-sub">Chaque recette coûte de l’or et, souvent, des dés sacrifiés. Le Dé de l’aïeul ne se forge pas.</p>
    </header>
    <div class="forge-recipes" data-role="forge-recipes"></div>
  `;
  container.appendChild(wrap);

  // Liste en accordéon : le parcours de la recette choisie s'ouvre juste sous sa carte.
  const list = wrap.querySelector<HTMLElement>('[data-role="forge-recipes"]') as HTMLElement;
  if (recipes.length === 0) list.innerHTML = '<p class="forge-note">Aucune recette disponible.</p>';
  for (const r of recipes) {
    list.insertAdjacentHTML('beforeend', recipeCardHtml(game, r, target?.id ?? null));
    if (r.unlocked && r.id === session.recipe) {
      list.appendChild(buildWorkspace(ctx, r, refresh));
      if (result) list.appendChild(buildResult(ctx, result));
    }
  }
  if (!session.recipe && recipes.length > 0) {
    list.insertAdjacentHTML('beforebegin', '<p class="forge-note forge-hint" data-role="forge-hint">Choisissez une recette pour commencer.</p>');
  }

  // ── Événements (délégation : l'onglet est reconstruit à chaque changement) ─
  wrap.addEventListener('click', (e) => {
    const el = e.target as HTMLElement | null;
    if (!el) return;
    const act = (name: string): HTMLElement | null => el.closest<HTMLElement>(name);

    const recipeBtn = act('[data-role="forge-recipe"]');
    if (recipeBtn && !recipeBtn.hasAttribute('disabled')) {
      const id = recipeBtn.dataset.recipe as ForgeRecipeId;
      if (session.recipe !== id) {
        session.recipe = id;
        session.result = null;
        // Le dé cible est gardé d'une recette à l'autre ; le reste repart de zéro.
        const keep = session.dieId;
        resetBelowTarget();
        session.dieId = keep;
        session.targetOpen = keep === null;
        const kept = findDie(game, keep);
        // Relance : le seul affixe relançable, s'il est déjà fixé, est choisi d'office.
        if (id === 'reroll' && kept) session.affixIndex = forgeMarksOf(kept).rerollSlot;
      }
      return refresh();
    }
    const targetEl = act('[data-role="forge-target"]');
    if (targetEl?.dataset.dieId) {
      // Toucher le dé déjà choisi (liste repliée) rouvre la liste.
      if (session.dieId === targetEl.dataset.dieId && !session.targetOpen) {
        session.targetOpen = true;
        return refresh();
      }
      if (session.dieId !== targetEl.dataset.dieId) {
        session.dieId = targetEl.dataset.dieId;
        session.result = null;
        resetBelowTarget();
        const die = findDie(game, session.dieId);
        // Relance : le seul affixe relançable, s'il est déjà fixé, est choisi d'office.
        if (session.recipe === 'reroll' && die) session.affixIndex = forgeMarksOf(die).rerollSlot;
      }
      session.targetOpen = false;
      return refresh();
    }
    if (act('[data-action="forge-change-target"]')) {
      session.targetOpen = true;
      return refresh();
    }
    const affixBtn = act('[data-role="forge-affix"]');
    if (affixBtn?.dataset.index !== undefined) {
      session.affixIndex = Number(affixBtn.dataset.index);
      session.result = null;
      return refresh();
    }
    const otherEl = act('[data-role="forge-other"]');
    if (otherEl?.dataset.dieId) {
      if (session.otherId !== otherEl.dataset.dieId) {
        session.otherId = otherEl.dataset.dieId;
        session.otherAffixIndex = null;
      }
      return refresh();
    }
    const otherAffix = act('[data-role="forge-other-affix"]');
    if (otherAffix?.dataset.index !== undefined) {
      session.otherAffixIndex = Number(otherAffix.dataset.index);
      return refresh();
    }
    if (act('[data-action="forge-apply"]')) return applyRecipe(ctx, refresh);
  });

  // Les cartes et lignes cliquables se valident aussi au clavier (Entrée, Espace).
  wrap.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-role="forge-target"], [data-role="forge-other"]');
    if (!el || el !== e.target) return;
    e.preventDefault();
    el.click();
  });

  wrap.addEventListener('change', (e) => {
    const el = e.target as HTMLElement | null;
    if (el instanceof HTMLSelectElement && el.dataset.role === 'forge-absolve-affix') {
      session.affixId = (el.value || null) as AffixId | null;
      session.consumed = new Set();
      session.consumeKey = '';
      session.result = null;
      return refresh();
    }
    if (el instanceof HTMLInputElement && el.dataset.role === 'forge-consume-box') {
      const id = el.dataset.dieId;
      if (!id) return;
      if (el.checked) session.consumed.add(id);
      else session.consumed.delete(id);
      return refresh();
    }
  });

  // Une Relance en attente (onglet quitté en cours de route, partie rechargée…) rouvre sa modale.
  ensureRerollModal(ctx, refresh);
}

/** Carte d'une recette dans la liste. */
function recipeCardHtml(game: Game, r: ForgeRecipeView, dieId: string | null): string {
  const price = forgePriceOf(game, r.id, dieId ?? undefined) ?? forgePriceOf(game, r.id);
  const selected = session.recipe === r.id;
  return `<button type="button" class="forge-recipe f-cut${selected ? ' is-selected' : ''}${r.unlocked ? '' : ' is-locked'}" data-role="forge-recipe" data-recipe="${r.id}" data-unlocked="${r.unlocked}" aria-pressed="${selected}"${r.unlocked ? '' : ' disabled'}>
    <span class="forge-recipe-head"><span class="forge-recipe-name">${r.name}</span><b class="forge-recipe-price num" data-role="forge-recipe-price">${priceText(price)}</b></span>${formulaHtml(r.id)}
    <span class="forge-recipe-desc">${r.description}</span>
    <span class="forge-recipe-eats" data-role="forge-recipe-eats">Dés consommés : ${r.consumesText || '—'}</span>
    ${
      r.unlocked
        ? ''
        : `<span class="forge-recipe-lock" data-role="forge-recipe-lock">${icon('lock')}<span>Nécessite ${r.requiresNodeName || 'un nœud de l’Abîme'}</span></span>`
    }
  </button>`;
}

/** Panneau de travail de la recette choisie : étapes, prix, verdict, bouton. */
function buildWorkspace(ctx: ScreenCtx, recipe: ForgeRecipeView, refresh: () => void): HTMLElement {
  const { game } = ctx;
  const needs = recipeNeeds(recipe.id);
  const panel = document.createElement('section');
  panel.className = 'forge-work f-panel f-panel--gold';
  panel.setAttribute('data-role', 'forge-work');
  panel.setAttribute('data-recipe', recipe.id);

  const target = findDie(game, session.dieId);
  let html = `<h3 class="forge-work-title">${recipe.name}</h3><p class="forge-work-desc">${recipe.description}</p>`;

  // ── Étape 1 : le dé cible ─────────────────────────────────────────
  const { dice, hidden, total } = targetDice(game);
  let listOpen = false;
  if (total === 0) {
    html += stepHtml(
      'Dé cible',
      `<p class="forge-note" data-role="forge-empty">Aucun dé à forger. ${NOT_FORGEABLE_TEXT} Tirez-en à la Boutique, sous-onglet « Corrupted ».</p>`,
      'target',
    );
  } else if (target && !session.targetOpen) {
    html += stepHtml(
      'Dé cible',
      `<ul class="forge-targets"><li class="die-card die-card--inventory die-card--tappable f-cut is-picked" data-rarity="${target.rarity}" data-role="forge-target" data-picked="true" data-die-id="${target.id}" tabindex="0" role="button" aria-pressed="true" aria-label="Dé cible : ${target.name}">${dieCardInnerHtml(target, '', game)}</li></ul>
       <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="forge-change-target">Changer de dé</button>`,
      'target',
    );
  } else {
    listOpen = true;
    html += stepHtml(
      'Dé cible',
      `<p class="forge-note">Touchez le dé à transformer (les dés équipés sont permis).</p>
       <ul class="forge-targets" data-role="forge-targets">${dice
         .map((d) => {
           const slot = game.state.equipped.indexOf(d.id);
           return `<li class="die-card die-card--inventory die-card--tappable f-cut${d.id === session.dieId ? ' is-picked' : ''}" data-rarity="${d.rarity}" data-role="forge-target" data-die-id="${d.id}" tabindex="0" role="button" aria-label="Choisir ${d.name}">${dieCardInnerHtml(d, '', game)}${
             slot >= 0 ? `<p class="die-card-equipped">${icon('check')}<span>Équipé — emplacement ${slot + 1}</span></p>` : ''
           }</li>`;
         })
         .join('')}</ul>`,
      'target',
    );
  }

  // ── Étape 2 : l'affixe du dé cible (Relance, Fusion) ───────────────
  if (target && needs.affix) {
    const title = recipe.id === 'fuse' ? 'Affixe conservé du dé cible' : 'Affixe à relancer';
    html += stepHtml(title, affixButtonsHtml(target, game, 'forge-affix', session.affixIndex), 'affix');
  }

  // ── Étape 3 : le second dé et son affixe (Fusion) ──────────────────
  let others: MasterDie[] = [];
  if (target && needs.other) {
    others = forgeCandidatesOf(game, recipe.id, target.id);
    const other = findDie(game, session.otherId);
    if (other && !others.some((d) => d.id === other.id)) {
      session.otherId = null;
      session.otherAffixIndex = null;
    }
    const chosen = findDie(game, session.otherId);
    html += stepHtml(
      'Second dé',
      others.length === 0
        ? '<p class="forge-note" data-role="forge-other-empty">Aucun second dé compatible (même rareté, même Matériau, Rare ou plus, jamais déjà fusionné).</p>'
        : `<ul class="forge-rows" data-role="forge-others">${others
            .map(
              (d) =>
                `<li class="forge-row f-cut${d.id === session.otherId ? ' is-picked' : ''}" data-rarity="${d.rarity}" data-role="forge-other" data-die-id="${d.id}" tabindex="0" role="button" aria-pressed="${d.id === session.otherId}">${rowInnerHtml(d, game)}</li>`,
            )
            .join('')}</ul>`,
      'other',
    );
    if (chosen) {
      html += stepHtml('Affixe conservé du second dé', affixButtonsHtml(chosen, game, 'forge-other-affix', session.otherAffixIndex), 'other-affix');
    }
  }

  // ── Étape 4 : l'affixe à ajouter (Absolution) ──────────────────────
  if (target && needs.absolve) {
    const pool = affixPoolFor(target.sides);
    html += stepHtml(
      'Affixe à ajouter',
      `<label class="forge-field"><select class="f-select" data-role="forge-absolve-affix" aria-label="Affixe à ajouter">
         <option value="">Choisir un affixe…</option>
         ${pool.map((a) => `<option value="${a.id}"${a.id === session.affixId ? ' selected' : ''}>${a.name}</option>`).join('')}
       </select></label>
       <p class="forge-note">Un seul affixe d’Absolution par dé ; il ne se relance jamais.</p>`,
      'absolve',
    );
  }

  // ── Étape 5 : les dés sacrifiés, pré-cochés ────────────────────────
  const askConsume = needs.consume > 0 && !!target && (!needs.absolve || session.affixId !== null);
  let candidates: MasterDie[] = [];
  if (askConsume && target) {
    candidates = forgeCandidatesOf(game, recipe.id, target.id, needs.absolve ? (session.affixId ?? undefined) : undefined);
    const key = `${recipe.id}|${target.id}|${session.affixId ?? ''}`;
    const ids = new Set(candidates.map((d) => d.id));
    if (session.consumeKey !== key) {
      // Du moins bon au meilleur : on coche les premiers, autant que la recette en demande.
      session.consumed = new Set(preselectConsumed(recipe.id, candidates, needs.consume));
      session.consumeKey = key;
    } else {
      session.consumed = new Set([...session.consumed].filter((id) => ids.has(id)));
    }
    html += stepHtml(
      `Dés sacrifiés <span class="forge-count" data-role="forge-consume-count">${session.consumed.size} / ${needs.consume}</span>`,
      candidates.length === 0
        ? `<p class="forge-note" data-role="forge-consume-empty">Aucun dé ne peut être sacrifié pour cette recette (${recipe.consumesText || 'dés non équipés, jamais le Dé de l’aïeul'} ; seuls les dés des Corrupted GachaDice se sacrifient).</p>`
        : `<ul class="forge-rows" data-role="forge-consumables">${candidates
            .map((d) => {
              const on = session.consumed.has(d.id);
              return `<li class="forge-row f-cut${on ? ' is-picked' : ''}" data-rarity="${d.rarity}" data-role="forge-consume" data-die-id="${d.id}"><label class="forge-row-label"><input type="checkbox" class="f-check" data-role="forge-consume-box" data-die-id="${d.id}"${on ? ' checked' : ''} aria-label="Sacrifier ${d.name}">${rowInnerHtml(d, game)}</label></li>`;
            })
            .join('')}</ul>`,
      'consume',
    );
  }

  // ── Résumé : prix, verdict, bouton ──────────────────────────────────
  const choices: ForgeChoices = {
    dieId: target?.id ?? null,
    affixIndex: session.affixIndex,
    otherId: session.otherId,
    otherAffixIndex: session.otherAffixIndex,
    affixId: session.affixId,
    consumedIds: [...session.consumed],
  };
  const req = buildRequest(recipe.id, choices);
  const verdict = req ? forgeCheckOf(game, req) : null;
  const price = forgePriceOf(game, recipe.id, target?.id) ?? (verdict && verdict.cost > 0 ? verdict.cost : null);
  let message: string;
  let ok = false;
  if (!target) message = 'Choisissez le dé cible.';
  else if (needs.affix && session.affixIndex === null) message = 'Choisissez un affixe.';
  else if (needs.other && !session.otherId) message = 'Choisissez le second dé.';
  else if (needs.other && session.otherAffixIndex === null) message = 'Choisissez l’affixe du second dé.';
  else if (needs.absolve && !session.affixId) message = 'Choisissez l’affixe à ajouter.';
  else if (verdict) {
    ok = verdict.ok;
    message = verdict.ok ? 'Prêt à forger.' : (verdict.reason ?? 'Cette recette est impossible.');
  } else message = 'Recette incomplète.';

  html += `<div class="forge-summary">
      <p class="forge-price" data-role="forge-price">Prix : <b class="num">${priceText(price)}</b>${
        recipe.id === 'temper' ? ' <span class="forge-price-formula" data-role="forge-price-formula">· suit le niveau du dé</span>' : ''
      }</p>
      <p class="forge-verdict${ok ? ' is-ok' : ''}" data-role="forge-verdict" data-ok="${ok}" aria-live="polite">${ok ? icon('check') : icon('lock')}<span>${message}</span></p>
      <button type="button" class="f-btn f-btn--primary f-btn--block" data-action="forge-apply"${ok ? '' : ' disabled'}>Forger</button>
    </div>`;

  panel.innerHTML = html;

  // A119 : le panneau « Tri et filtres » coiffe la liste des cibles quand elle est dépliée.
  if (listOpen) {
    const step = panel.querySelector<HTMLElement>('[data-step="target"]');
    const view = readDiceView();
    const head: HTMLElement[] = [
      buildDiceViewPanel({
        onChange: refresh,
        materials: presentMaterials(game.state.inventory),
        sides: presentSides(game.state.inventory),
      }),
    ];
    const notice = buildHiddenNotice(hasActiveFilter(view) ? hidden : 0, refresh);
    if (notice) head.push(notice);
    step?.querySelector('.forge-step-title')?.after(...head);
  }
  return panel;
}

/** Carte du dé résultat et ligne de bilan, sous le panneau. */
function buildResult(ctx: ScreenCtx, result: NonNullable<ForgeSession['result']>): HTMLElement {
  const { game } = ctx;
  const die = findDie(game, result.dieId);
  const section = document.createElement('section');
  section.className = 'forge-result f-panel';
  section.setAttribute('data-role', 'forge-result');
  section.setAttribute('data-recipe', result.recipe);
  section.innerHTML = `
    <h3 class="forge-work-title">Dé forgé</h3>
    <p class="forge-note" data-role="forge-result-note">${result.note}${result.cost > 0 ? ` · ${formatGold(result.cost)} or dépensés` : ''}</p>
    ${die ? `<ul class="forge-targets"><li class="die-card die-card--inventory f-cut" data-rarity="${die.rarity}" data-die-id="${die.id}">${dieCardInnerHtml(die, '', game)}</li></ul>` : ''}`;
  return section;
}

/** « Forger » : applique la recette ; les écrans se redessinent par les événements du cœur. */
function applyRecipe(ctx: ScreenCtx, refresh: () => void): void {
  const { game } = ctx;
  const recipeId = session.recipe;
  const target = findDie(game, session.dieId);
  if (!recipeId || !target) return;
  const req = buildRequest(recipeId, {
    dieId: target.id,
    affixIndex: session.affixIndex,
    otherId: session.otherId,
    otherAffixIndex: session.otherAffixIndex,
    affixId: session.affixId,
    consumedIds: [...session.consumed],
  });
  if (!req) return;
  const verdict = forgeCheckOf(game, req);
  if (!verdict.ok) {
    toast(verdict.reason ?? 'Cette recette est impossible.', 2800, 'lock');
    return refresh();
  }
  const affixId = session.affixId;
  const out = forgeApplyOf(game, req);
  if (!out) {
    toast('La Forge a refusé la recette.', 2800, 'lock');
    return refresh();
  }

  // Les dés sacrifiés ont quitté l'inventaire : la sélection repart de zéro.
  session.consumed = new Set();
  session.consumeKey = '';
  session.otherId = null;
  session.otherAffixIndex = null;

  if (out.recipe === 'reroll' && out.options) {
    // La modale de Relance conclut ; le bilan s'écrit après le choix.
    session.result = null;
    refresh();
    ensureRerollModal(ctx, refresh);
    return;
  }

  const fresh = findDie(game, out.die.id) ?? out.die;
  // Fusion : un dé neuf remplace la cible ; les autres recettes gardent le dé pour la suite.
  session.dieId = out.recipe === 'fuse' ? fresh.id : session.dieId;
  const note = outcomeNote(out, affixId);
  session.result = { recipe: out.recipe, name: fresh.name, dieId: fresh.id, cost: out.cost, note };
  toast(note, 2800, out.recipe === 'sublimate' && out.success === false ? 'lock' : 'check');
  refresh();
}
